/**
 * /referrals/ — the referral form (inc/referrals.php renders it, this file
 * enhances it). Without JS the form posts normally and the server redirects
 * back with the thank-you; with JS it posts in place and the thank-you
 * replaces the form without a page load.
 *
 * Analytics: three dataLayer events — referral_view, referral_form_start,
 * referral_form_submit. GTM (proper-scripts) picks them up; on stage/dev,
 * where no tags load, the pushes are harmless.
 */

const root = document.querySelector( '[data-mp-referral]' );
const form = root?.querySelector( 'form' );

function track( event, extra = {} ) {
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push( { event, ...extra } );
}

function fieldError( name, message ) {
	const input = form.querySelector( `[name="${ name }"]` );
	const id = name === 'mention' ? 'ref-mention-err' : `ref-${ name.replace( /_/g, '-' ) }-err`;
	const slot = form.querySelector( `#${ id }` );
	if ( slot ) {
		slot.textContent = message || '';
	}
	if ( input && input.type !== 'radio' ) {
		if ( message ) {
			input.setAttribute( 'aria-invalid', 'true' );
		} else {
			input.removeAttribute( 'aria-invalid' );
		}
	}
	return input;
}

function clearErrors() {
	form.querySelectorAll( '.mp-ref-err' ).forEach( ( el ) => ( el.textContent = '' ) );
	form.querySelectorAll( '[aria-invalid]' ).forEach( ( el ) => el.removeAttribute( 'aria-invalid' ) );
	const summary = form.querySelector( '.mp-ref-summary' );
	summary.hidden = true;
	summary.textContent = '';
}

/** Client-side checks — the server repeats every one of them. */
function validate() {
	const errors = {};
	const value = ( name ) => ( form.elements[ name ]?.value || '' ).trim();
	const email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

	if ( ! value( 'ref_name' ) ) errors.ref_name = 'Enter your name.';
	if ( ! value( 'ref_company' ) ) errors.ref_company = 'Enter your company.';
	if ( ! email.test( value( 'ref_email' ) ) ) errors.ref_email = 'Enter your email address, like name@company.com.';
	if ( ! value( 'biz_name' ) ) errors.biz_name = 'Enter the business name.';
	if ( ! value( 'contact_name' ) ) errors.contact_name = 'Enter a contact name.';
	if ( ! email.test( value( 'contact_email' ) ) ) {
		errors.contact_email = 'Enter their email address, like name@company.com.';
	} else if ( value( 'contact_email' ).toLowerCase() === value( 'ref_email' ).toLowerCase() ) {
		errors.contact_email = 'This is your own email. Enter the email of the person you are referring.';
	}
	if ( ! form.querySelector( '[name="mention"]:checked' ) ) errors.mention = 'Choose yes or no.';
	if ( ! form.elements.terms.checked ) errors.terms = 'Please agree to the program terms.';
	return errors;
}

function showErrors( errors ) {
	let first = null;
	Object.entries( errors ).forEach( ( [ name, message ] ) => {
		const input = fieldError( name, message );
		first = first || input;
	} );
	const count = Object.keys( errors ).length;
	const summary = form.querySelector( '.mp-ref-summary' );
	summary.textContent = count === 1 ? 'One field needs attention.' : `${ count } fields need attention.`;
	summary.hidden = false;
	first?.focus();
}

function showThanks( thanks ) {
	const done = root.querySelector( '.mp-ref-done' );
	done.querySelector( '[data-ref-first]' ).textContent = thanks.first ? `, ${ thanks.first }` : '';
	done.querySelector( '[data-ref-contact]' ).textContent = thanks.contact || '';
	done.querySelector( '[data-ref-business]' ).textContent = thanks.business || '';
	form.hidden = true;
	done.hidden = false;
	done.focus();
	done.scrollIntoView( { block: 'start', behavior: window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ? 'auto' : 'smooth' } );
}

function resetForAnother() {
	// "About you" stays — the same person is likely to refer again.
	[ 'biz_name', 'contact_name', 'contact_email', 'contact_phone', 'biz_website', 'notes' ].forEach( ( name ) => {
		form.elements[ name ].value = '';
	} );
	form.querySelectorAll( '[name="needs[]"], [name="reward"]' ).forEach( ( el ) => ( el.checked = false ) );
	form.querySelector( '[name="mention"][value="yes"]' ).checked = true;
	form.elements.ts.value = String( Date.now() );
	updateCount();
	clearErrors();
	root.querySelector( '.mp-ref-done' ).hidden = true;
	form.hidden = false;
	form.elements.biz_name.focus();
}

const notes = form?.elements.notes;
const counter = form?.querySelector( '#ref-notes-count' );
function updateCount() {
	if ( notes && counter ) {
		const left = 500 - notes.value.length;
		counter.textContent = notes.value.length ? `${ left } characters left` : '500 characters max';
	}
}

if ( root && form ) {
	track( 'referral_view', { referral_code: form.elements.ref_code.value ? 'yes' : 'no' } );

	// Stamp the time-trap here too: main.js does it for every [data-mp-form],
	// but this runs deferred and must not depend on load order.
	if ( ! form.elements.ts.value ) {
		form.elements.ts.value = String( Date.now() );
	}

	let started = false;
	form.addEventListener( 'focusin', () => {
		if ( ! started ) {
			started = true;
			track( 'referral_form_start' );
		}
	} );

	notes?.addEventListener( 'input', updateCount );

	// Clear a field's error as soon as it is edited.
	form.addEventListener( 'input', ( e ) => {
		if ( e.target.name ) {
			fieldError( e.target.name, '' );
		}
	} );

	form.addEventListener( 'submit', async ( e ) => {
		e.preventDefault();
		clearErrors();

		const errors = validate();
		if ( Object.keys( errors ).length ) {
			showErrors( errors );
			return;
		}

		const button = form.querySelector( 'button[type="submit"]' );
		const label = button.textContent;
		button.disabled = true;
		button.textContent = 'Sending…';

		const body = new FormData( form );
		body.append( '_ajax', '1' );

		try {
			// getAttribute, not form.action: the hidden <input name="action">
			// WordPress needs shadows the property and fetch would post to
			// "[object HTMLInputElement]".
			const res = await fetch( form.getAttribute( 'action' ), { method: 'POST', body, credentials: 'same-origin' } );
			const json = await res.json();
			if ( json.ok ) {
				track( 'referral_form_submit' );
				showThanks( json.thanks || {} );
			} else {
				showErrors( json.errors || {} );
			}
		} catch {
			// Network or parse failure: fall back to a plain post so the
			// referral is not lost to a JS problem.
			form.submit();
			return;
		} finally {
			button.disabled = false;
			button.textContent = label;
		}
	} );

	root.querySelector( '[data-ref-again]' )?.addEventListener( 'click', ( e ) => {
		e.preventDefault();
		resetForAnother();
	} );
}
